/**
 * Onboarding — first-run experience. Per Design Brief §6h: manager-tier user
 * is the most common entry; HR-tier admin gets a longer setup; white-label
 * partner has their own flow.
 *
 * V1 first ship: 4-step wizard for the manager-tier path. HR-tier and
 * partner branches add additional steps inline.
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../lib/useSession.js";
import { SectionHeader, Callout, NumberedList, CheckList } from "../components/index.js";

const STEPS = [
  { id: "welcome",      label: "Welcome" },
  { id: "first-profile",label: "Add your first Profile" },
  { id: "calendar",     label: "Connect calendar (optional)" },
  { id: "blueprint",    label: "Generate your first Blueprint" },
];

export default function OnboardingPage() {
  const { session, appUser } = useSession();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);

  if (!session) return <p style={{ padding: 24 }}>Sign in to start onboarding.</p>;

  const isHrTier = appUser?.tier === "hr_director" || appUser?.is_hr_admin;

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: "32px 24px" }}>
      {/* Progress strip */}
      <div style={{ display: "flex", gap: 4, marginBottom: 28 }}>
        {STEPS.map((s, i) => (
          <div
            key={s.id}
            style={{
              flex: 1,
              height: 6,
              background: i <= step
                ? "var(--ppr-color-accent-navy)"
                : "var(--ppr-color-border-default)",
            }}
          />
        ))}
      </div>

      <div
        style={{
          fontFamily: "var(--ppr-typography-font-family-mono)",
          fontSize: "11px",
          letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
          textTransform: "uppercase",
          color: "var(--ppr-color-text-muted)",
          marginBottom: 8,
        }}
      >
        Step {step + 1} of {STEPS.length} — {STEPS[step].label}
      </div>

      {step === 0 && <WelcomeStep onNext={() => setStep(1)} appUser={appUser} isHrTier={isHrTier} />}
      {step === 1 && <FirstProfileStep onNext={() => setStep(2)} onSkip={() => setStep(2)} />}
      {step === 2 && <CalendarStep onNext={() => setStep(3)} onSkip={() => setStep(3)} />}
      {step === 3 && <BlueprintStep onFinish={() => navigate("/")} />}

      <div style={{ marginTop: 28, display: "flex", justifyContent: "space-between" }}>
        {step > 0 ? (
          <button onClick={() => setStep(step - 1)} style={ghostBtn}>← Back</button>
        ) : <span />}
        <Link to="/" style={ghostLink}>Skip onboarding</Link>
      </div>
    </div>
  );
}

function WelcomeStep({ onNext, appUser, isHrTier }) {
  return (
    <div>
      <h1 style={titleStyle}>Welcome to Interaction Engineering.</h1>
      <p style={paragraphStyle}>
        {appUser?.display_name ? `Hi ${appUser.display_name}. ` : ""}
        Three things this tool does well:
      </p>
      <NumberedList
        items={[
          <span><strong>Pre-Meeting Blueprints</strong> — read the night before, walk into the room different.</span>,
          <span><strong>Playbooks</strong> — a living read on each person you work with, that gets sharper over time.</span>,
          <span><strong>Relationship + Room Blueprints</strong> — for the dynamics and rooms where one meeting won't cut it.</span>,
        ]}
      />

      <Callout.Remember>
        {isHrTier
          ? "You're on the Pro tier. You get the consistency audit surface, the self-view share option, and unlimited Blueprints. Treat this setup as the standard flow plus the Pro capabilities at the end."
          : "Setup takes about three minutes. Skip anything you want to come back to."}
      </Callout.Remember>

      <button onClick={onNext} style={primaryBtn}>Let's go</button>
    </div>
  );
}

function FirstProfileStep({ onNext, onSkip }) {
  return (
    <div>
      <h1 style={titleStyle}>Add your first Profile.</h1>
      <p style={paragraphStyle}>
        A Profile is one person you're reading — a prospect, a counterpart, anyone you're about to deal with. Enter their <strong>date of birth</strong> and the full behavioral read generates instantly.
      </p>

      <CheckList items={[
        <span>No invite, no waiting — the read is ready the moment you save.</span>,
        <span>Day, month, and year give you the complete read (generation and decan included).</span>,
        <span>Reuse a Profile across every Blueprint — read someone once, pull them into any play.</span>,
      ]} />

      <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
        <Link to="/profiles/new" style={primaryLink}>Add a Profile</Link>
        <button onClick={onSkip} style={ghostBtn}>Skip</button>
      </div>
    </div>
  );
}

function CalendarStep({ onNext, onSkip }) {
  return (
    <div>
      <h1 style={titleStyle}>Connect your calendar.</h1>
      <p style={paragraphStyle}>
        Optional but high-leverage. With a calendar connected, Interaction Engineering scans the next 14 days and pre-generates Pre-Meeting Blueprints 12 hours before each meeting that has a Profile match. The "open the app the night before" habit becomes the app showing up <em>for</em> you.
      </p>

      <Callout.Tip>
        Read-only access. We never write to your calendar. We hash the account email before storing it.
      </Callout.Tip>

      <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
        <Link to="/settings/integrations" style={primaryLink}>Open settings</Link>
        <button onClick={onSkip} style={ghostBtn}>Skip for now</button>
      </div>
    </div>
  );
}

function BlueprintStep({ onFinish }) {
  return (
    <div>
      <h1 style={titleStyle}>Generate your first Blueprint.</h1>
      <p style={paragraphStyle}>
        Pick the Blueprint type that matches the conversation you've got coming up:
      </p>

      <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
        <Link to="/blueprints/conflict-mediation" style={blueprintCardStyle}>
          <strong>Conflict Mediation Brief <span style={newBadgeStyle}>NEW</span></strong>
          <span style={subTextStyle}>Two people stuck — and you're being asked to step in. Third-party mediator vantage. Pro tier.</span>
        </Link>
        <Link to="/blueprints/pre-meeting" style={blueprintCardStyle}>
          <strong>Pre-Meeting Blueprint</strong>
          <span style={subTextStyle}>One-page prep for tomorrow's meeting</span>
        </Link>
        <Link to="/blueprints/relational" style={blueprintCardStyle}>
          <strong>Relational Blueprint</strong>
          <span style={subTextStyle}>The dynamic between two people, beyond a single meeting</span>
        </Link>
        <Link to="/blueprints/counterpart-breakdown" style={blueprintCardStyle}>
          <strong>Counterpart Breakdown</strong>
          <span style={subTextStyle}>Drop in a bio or background + the person's DOB, get a one-screen card plus the full briefing</span>
        </Link>
        <Link to="/blueprints/room" style={blueprintCardStyle}>
          <strong>Room Blueprint <span style={newBadgeStyle}>NEW</span></strong>
          <span style={subTextStyle}>Read a whole room — a buying committee, panel, or leadership team. Who decides, who blocks, who to win first. Pro tier.</span>
        </Link>
        <Link to="/blueprints/negotiation" style={blueprintCardStyle}>
          <strong>Negotiation Blueprint <span style={newBadgeStyle}>NEW</span></strong>
          <span style={subTextStyle}>Read the person across the table — what they're optimizing for, where their give is, how they concede, your move sequence. Pro tier.</span>
        </Link>
        <Link to="/blueprints/common-ground" style={blueprintCardStyle}>
          <strong>Common Ground <span style={newBadgeStyle}>NEW</span></strong>
          <span style={subTextStyle}>Explore Alignment: a real Common Goal and Common Enemy to build rapport fast, keyed to how they're wired. Pro tier.</span>
        </Link>
        <Link to="/blueprints/thought-engineering" style={blueprintCardStyle}>
          <strong>Thought Engineering <span style={newBadgeStyle}>NEW</span></strong>
          <span style={subTextStyle}>Strategy &amp; design — the 5-step plan to engineer the whole encounter before the first word. The capstone. Pro tier.</span>
        </Link>
      </div>

      <button onClick={onFinish} style={{ ...primaryBtn, marginTop: 20 }}>Finish onboarding</button>
    </div>
  );
}

const titleStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-3xl)",
  color: "var(--ppr-color-accent-navy)",
  letterSpacing: "var(--ppr-typography-letter-spacing-snug)",
  lineHeight: "var(--ppr-typography-line-height-tight)",
  margin: "0 0 16px",
};

const paragraphStyle = {
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "var(--ppr-typography-font-size-base)",
  lineHeight: "var(--ppr-typography-line-height-relaxed)",
  color: "var(--ppr-color-text-secondary)",
  marginBottom: 20,
};

const subTextStyle = {
  display: "block",
  fontFamily: "var(--ppr-typography-font-family-body)",
  fontSize: "13px",
  color: "var(--ppr-color-text-secondary)",
  marginTop: 4,
};

const blueprintCardStyle = {
  padding: "14px 16px",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  textDecoration: "none",
  color: "var(--ppr-color-text-primary)",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "16px",
};

const newBadgeStyle = {
  display: "inline-block",
  marginLeft: 8,
  padding: "2px 6px",
  background: "var(--ppr-color-accent-coral)",
  color: "var(--ppr-color-text-inverse)",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "9px",
  letterSpacing: "0.08em",
  verticalAlign: "middle",
};

const primaryBtn = {
  padding: "12px 18px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-accent-navy)",
  color: "var(--ppr-color-text-inverse)",
  cursor: "pointer",
  marginTop: 16,
};

const primaryLink = {
  display: "inline-block",
  padding: "12px 18px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-accent-navy)",
  color: "var(--ppr-color-text-inverse)",
  textDecoration: "none",
};

const ghostBtn = {
  padding: "10px 16px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-secondary)",
  cursor: "pointer",
};

const ghostLink = {
  fontFamily: "var(--ppr-typography-font-family-mono)",
  fontSize: "12px",
  color: "var(--ppr-color-text-muted)",
  textDecoration: "none",
};
